import Button from "components/components/Button/Button";
import Checkbox from "components/components/CheckboxInput/Checkbox";
import { DropdownChevron } from "components/components/Icon/Icon";
import { RequestStatus } from "hooks/useRequest";
import React from "react";
import { ReactNode, useMemo } from "react";
import colours from "styles/colours";

import { FilterOption, mapToOptions } from "./helpers";
import {
  BoldedLabel,
  BoldWord,
  Divider,
  InputContainer,
  AutoInputContainer,
  MiniInputContainer,
  Placeholder,
  SelectAllRow,
  StyledLabel,
  StyledMultiSelect,
  ValueRendererLabel,
} from "./style";

type InputSize = "mini" | "auto";

interface MultiSelectDropdownProps {
  values: string[];
  selectedValues: string[];
  onChange: (value: FilterOption[]) => void;
  status: RequestStatus;
  label?: string;
  disableSearchBar?: boolean;
  size?: InputSize;
}

interface ItemRendererProps {
  checked: boolean;
  option: FilterOption;
  onClick: () => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  values,
  selectedValues,
  onChange,
  status,
  label,
  disableSearchBar = true,
  size,
}) => {
  const allOptions = useMemo(() => mapToOptions(values), [values]);
  const selectedOptions = useMemo(() => {
    if (selectedValues.length === 0) {
      return [];
    }
    return mapToOptions(selectedValues);
  }, [selectedValues]);

  const customValueRenderer = (
    selected: FilterOption[],
    _options: FilterOption[]
  ): ReactNode => (
    <ValueRendererLabel>
      {selected.length === _options.length ? (
        <>
          Selected filters:&nbsp;<BoldWord>All</BoldWord>
        </>
      ) : selected.length ? (
        <>
          Selected filters:&nbsp;<BoldWord>{selected.length}</BoldWord>
        </>
      ) : (
        <Placeholder>
          {disableSearchBar ? "Select option" : "Search or select"}
        </Placeholder>
      )}
    </ValueRendererLabel>
  );

  const customItemRenderer: React.FC<ItemRendererProps> = ({
    checked,
    option,
    onClick,
  }) => (
    <>
      {option.label === "Select All" ? (
        <SelectAllRow>
          <Button
            className="link-inline"
            buttonText={option.label}
            onClick={(e): void => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }}
          />
          <Divider />
          <Button
            className="link-inline"
            buttonText="Deselect All"
            onClick={(e): void => {
              e.preventDefault();
              e.stopPropagation();
              onChange([]);
            }}
          />
        </SelectAllRow>
      ) : (
        <div className="item-renderer">
          <Checkbox
            inputId={`toggle-hidden-boal-flags-${option.value}`}
            isChecked={checked}
            isInline
            onChange={onClick}
          />
          <StyledLabel>{option.label}</StyledLabel>
        </div>
      )}
    </>
  );

  const customDropdownIndicator = (): JSX.Element => (
    <DropdownChevron
      colour={colours.elexonBlue}
      className="elx-multiselect-chevron"
    />
  );

  const containerMap: Record<string, React.ElementType> = {
    mini: MiniInputContainer,
    auto: AutoInputContainer,
  };

  const Container: React.ElementType = size
    ? containerMap[size]
    : InputContainer;

  return (
    <Container>
      {label && <BoldedLabel>{label}</BoldedLabel>}
      <StyledMultiSelect
        options={allOptions}
        value={selectedOptions}
        onChange={onChange}
        labelledBy="Select"
        valueRenderer={customValueRenderer}
        ItemRenderer={customItemRenderer}
        ArrowRenderer={customDropdownIndicator}
        ClearSelectedIcon={null}
        disableSearch={disableSearchBar}
        isLoading={status === RequestStatus.IN_PROGRESS}
      />
    </Container>
  );
};

export default MultiSelectDropdown;
